import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SurveyPlanInput,
  useErpSurveyTargetingQuery,
  useSaveSurveyTargetingMutation,
} from 'core/graphql';

import {
  ISurveyPlan,
  ISurveyPlanDraft,
  ISurveyTargetingResult,
  planToDraft,
  surveyErrorMessage,
} from './surveyModel';

interface ISaveResult {
  success: boolean;
  /** Localized PLAN_LOCKED / INVALID_PLAN / … — rule-level failure only. */
  errorMessage?: string;
  /**
   * Present on SUCCESS too: the per-group report. A populated `skipped[]`
   * with `success: true` is the normal outcome — the rules saved and some
   * groups simply have no lesson N.
   */
  result?: ISurveyTargetingResult;
}

interface ISurveyTargetingHook {
  plans: ISurveyPlan[];
  drafts: ISurveyPlanDraft[];
  loading: boolean;
  pending: boolean;
  save: (surveyId: string, drafts: ISurveyPlanDraft[]) => Promise<ISaveResult>;
}

const draftToInput = (draft: ISurveyPlanDraft): SurveyPlanInput => ({
  id: draft.id,
  scope: draft.scope,
  courseId: draft.scope === 'course' ? draft.courseId : undefined,
  onlyActiveGroups: draft.onlyActiveGroups,
  triggerKind: draft.triggerKind,
  triggerLessonNumber:
    draft.triggerKind === 'lesson_number'
      ? draft.triggerLessonNumber
      : undefined,
  skipPastLessons: draft.skipPastLessons,
  isActive: draft.isActive,
  groupIds: draft.scope === 'groups' ? draft.groupIds : undefined,
});

/**
 * Spec 0027 — the survey's «Аудитория и триггер» block.
 *
 * ONE bulk mutation rather than N `assignSurveyToGroup` calls: removing a
 * group from a rule needs a diff against the previous state, which only the
 * server holds, and N calls would produce a half-saved audience the UI cannot
 * describe.
 *
 * Refetches the group card's assignment list and the /surveys counter so both
 * reflect the new rules the moment the builder saves.
 */
export const useSurveyTargeting = (surveyId?: string): ISurveyTargetingHook => {
  const { t } = useTranslation();
  const { data, loading } = useErpSurveyTargetingQuery({
    variables: { surveyId: surveyId as string },
    skip: !surveyId,
    fetchPolicy: 'cache-and-network',
  });
  const [saveRun] = useSaveSurveyTargetingMutation({
    // Deliberately NOT refetching 'ErpSurveyTargeting' by name: on a brand-new
    // survey that query is mounted skipped with `surveyId: undefined`, and a
    // by-name refetch re-runs it with those variables → HTTP 400 «Variable
    // '$surveyId' of required type 'ID!' was not provided», which the app's
    // errorLink turns into a /500 redirect right after a SUCCESSFUL save.
    // The mutation payload already carries the fresh plans, so it is redundant.
    refetchQueries: ['ErpGroupSurveyAssignments', 'ErpSurveysList'],
  });
  const [pending, setPending] = useState(false);

  const plans = data?.surveyTargeting?.plans || [];

  const save = useCallback<ISurveyTargetingHook['save']>(
    async (id, drafts) => {
      setPending(true);
      try {
        const result = await saveRun({
          variables: { surveyId: id, plans: drafts.map(draftToInput) },
        });
        const payload = result.data?.setSurveyTargeting;
        if (payload?.success) {
          return { success: true, result: payload.targeting || undefined };
        }
        return {
          success: false,
          errorMessage: surveyErrorMessage(t, payload?.customErrors),
        };
      } catch (e) {
        return { success: false, errorMessage: (e as Error).message };
      } finally {
        setPending(false);
      }
    },
    [saveRun, t],
  );

  return {
    plans,
    drafts: plans.map((plan, index) => planToDraft(plan, `saved-${index}`)),
    loading,
    pending,
    save,
  };
};
